import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { useUIStore, useAuthStore } from '@/store';
import { authService } from '@/services/auth.service';
import { useCreditBalance } from '@/hooks/useCredits';
import { useHasAny } from '@/hooks/useAutomations';
import { MaterialIcon } from '@/components/icons';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

/** `labelKey` resolves under `nav.*`; `lockedBeforeSetup` greys the item out until setup is done. */
type NavItem = { to: string; icon: string; labelKey: string; lockedBeforeSetup: boolean };

const topNav: NavItem[] = [
  { to: '/dashboard', icon: 'grid_view', labelKey: 'dashboard', lockedBeforeSetup: true },
  { to: '/enrichments', icon: 'auto_fix_high', labelKey: 'enrichments', lockedBeforeSetup: true },
  { to: '/studio', icon: 'auto_awesome', labelKey: 'studio', lockedBeforeSetup: true },
  { to: '/results', icon: 'cloud_download', labelKey: 'results', lockedBeforeSetup: true },
  { to: '/credits', icon: 'payments', labelKey: 'credits', lockedBeforeSetup: true },
  { to: '/setup', icon: 'rocket_launch', labelKey: 'setup', lockedBeforeSetup: false },
];

const automationsNav: NavItem = { to: '/automations', icon: 'bolt', labelKey: 'automations', lockedBeforeSetup: true };

const adminNav: NavItem[] = [
  { to: '/admin/automations', icon: 'bolt', labelKey: 'adminAutomations', lockedBeforeSetup: false },
  { to: '/admin/logs', icon: 'receipt_long', labelKey: 'adminLogs', lockedBeforeSetup: false },
  { to: '/admin/api', icon: 'api', labelKey: 'adminApi', lockedBeforeSetup: false },
  { to: '/admin/settings', icon: 'tune', labelKey: 'adminSettings', lockedBeforeSetup: false },
];

// Items still reachable but hidden from the top nav (profile popover + deep links).
// T-243: Settings and Webhook logs stay locked until setup is finished.
const profileMenu: NavItem[] = [
  { to: '/profile', icon: 'account_circle', labelKey: 'profile', lockedBeforeSetup: false },
  { to: '/settings', icon: 'settings', labelKey: 'settings', lockedBeforeSetup: true },
  { to: '/webhook-logs', icon: 'receipt_long', labelKey: 'webhookLogs', lockedBeforeSetup: true },
];

export function DashboardLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarExpanded, toggleSidebar, setSidebarExpanded } = useUIStore();
  const { user, clearAuth } = useAuthStore();
  const setupComplete = user?.setupComplete ?? false;
  const { data: credits } = useCreditBalance();
  const { data: hasAutomations } = useHasAny(setupComplete);
  const isAdmin = user?.isAdmin === true;
  const mainNav = hasAutomations
    ? [...topNav.slice(0, 3), automationsNav, ...topNav.slice(3)]
    : topNav;

  const collapsed = !sidebarExpanded;

  const helpSeenKey = `knox_help_seen_${user?.client_id ?? 'anon'}`;
  const [showHelpBanner, setShowHelpBanner] = useState(
    () => !localStorage.getItem(helpSeenKey)
  );

  const dismissBanner = () => {
    localStorage.setItem(helpSeenKey, '1');
    setShowHelpBanner(false);
  };

  useEffect(() => {
    if (location.pathname === '/help' && showHelpBanner) dismissBanner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const [profileOpen, setProfileOpen] = useState(false);
  useEffect(() => {
    setProfileOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await authService.signOut();
    clearAuth();
    navigate('/signin', { replace: true });
  };

  // Ensure sidebar stays expanded on desktop — the UIStore defaults to collapsed.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      setSidebarExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const freeRemaining = credits?.free ?? 0;

  return (
    <div className="relative min-h-screen bg-background text-brand-dark">
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between bg-primary px-4 py-3 shadow-sm">
        <button
          onClick={toggleSidebar}
          aria-label={t('nav.toggleMenu')}
          className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 text-white"
        >
          <span className="material-symbols-outlined">menu</span>
        </button>
        <div className="text-white font-bold tracking-tight">KNOX</div>
        <div className="w-10" />
      </header>

      {/* Backdrop on mobile */}
      {sidebarExpanded && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          onClick={toggleSidebar}
        />
      )}

      {/* Collapse toggle (desktop only) — sits OUTSIDE the sidebar, anchored to its right edge */}
      <button
        onClick={toggleSidebar}
        aria-label={t('nav.toggleSidebar')}
        className={clsx(
          'hidden lg:flex fixed top-7 z-[60] w-8 h-8 rounded-full',
          'bg-white text-primary shadow-lg items-center justify-center',
          'hover:scale-110 transition-all duration-300',
          collapsed ? 'left-[calc(5rem-1rem)]' : 'left-[calc(18rem-1rem)]'
        )}
      >
        <span
          className={clsx(
            'material-symbols-outlined text-xl transition-transform duration-300',
            collapsed && 'rotate-180'
          )}
        >
          chevron_left
        </span>
      </button>

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed left-0 top-0 h-screen z-50 flex flex-col bg-primary text-white',
          'transition-[width,padding,transform] duration-300',
          collapsed
            ? 'w-20 px-3 py-6 -translate-x-full lg:translate-x-0'
            : 'w-72 px-6 py-6 translate-x-0'
        )}
      >
        {/* Logo */}
        <div
          className={clsx(
            'mb-10 flex items-center',
            collapsed ? 'justify-center' : 'gap-4 px-1'
          )}
        >
          <Link
            to={setupComplete ? '/dashboard' : '#'}
            className="flex items-center gap-4"
          >
            <div className="w-14 h-14 min-w-[3.5rem] rounded-xl bg-white/10 overflow-hidden">
              <img
                src="/knoxlogo.png"
                alt="KNOX"
                className="w-full h-full object-cover"
              />
            </div>
            {!collapsed && (
              <div>
                <div className="text-xl text-white font-bold tracking-tight leading-tight">KNOX</div>
                <div className="text-[11px] uppercase tracking-widest text-white/60 leading-tight">
                  Data Magic
                </div>
              </div>
            )}
          </Link>
        </div>

        {/* Top nav */}
        <nav className="flex-1 flex flex-col">
          <div className="space-y-1">
            {mainNav.map(({ to, icon, labelKey, lockedBeforeSetup }) => {
              const label = t(`nav.${labelKey}`);
              const locked = !setupComplete && lockedBeforeSetup;
              if (locked) {
                return (
                  <div
                    key={to}
                    className={clsx(
                      'flex items-center gap-4 px-5 py-2 rounded-xl text-white/30 cursor-not-allowed',
                      collapsed && 'justify-center px-0'
                    )}
                    title={collapsed ? label : t('nav.lockedUntilSetup')}
                  >
                    <span className="material-symbols-outlined text-[26px]">{icon}</span>
                    {!collapsed && <span className="text-[15px]">{label}</span>}
                  </div>
                );
              }
              return (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    clsx(
                      'relative flex items-center gap-4 px-5 py-2 rounded-xl transition-all duration-200',
                      collapsed && 'justify-center px-0',
                      isActive
                        ? 'bg-white/10 text-inverse-primary'
                        : 'text-white/70 hover:bg-white/5 hover:text-white'
                    )
                  }
                  title={collapsed ? label : undefined}
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span className="absolute right-0 top-1 bottom-1 w-1 rounded-l bg-inverse-primary" />
                      )}
                      <span className="material-symbols-outlined text-[26px]">{icon}</span>
                      {!collapsed && (
                        <span className="text-[15px] whitespace-nowrap">{label}</span>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>

          {isAdmin && (
            <div className="mt-4 space-y-1">
              {!collapsed && (
                <div className="flex items-center gap-2 px-5 pb-1 text-[11px] uppercase tracking-widest text-white/50">
                  <span className="material-symbols-outlined text-base">admin_panel_settings</span>
                  {t('nav.admin')}
                </div>
              )}
              {collapsed && (
                <div className="flex justify-center py-1 text-white/50" title={t('nav.admin')}>
                  <span className="material-symbols-outlined text-[22px]">admin_panel_settings</span>
                </div>
              )}
              {adminNav.map(({ to, icon, labelKey }) => {
                const label = t(`nav.${labelKey}`);
                return (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center gap-4 px-5 py-2 rounded-xl transition-all duration-200',
                        collapsed && 'justify-center px-0',
                        isActive
                          ? 'bg-white/10 text-inverse-primary'
                          : 'text-white/70 hover:bg-white/5 hover:text-white',
                      )
                    }
                    title={collapsed ? label : undefined}
                  >
                    <span className="material-symbols-outlined text-[26px]">{icon}</span>
                    {!collapsed && <span className="text-[15px] whitespace-nowrap">{label}</span>}
                  </NavLink>
                );
              })}
            </div>
          )}

          {/* Free credits banner (middle of the sidebar) */}
          {!collapsed && setupComplete && (
            <div className="flex-1 flex items-center justify-center px-5 py-4">
              <div className="w-full bg-white/5 rounded-xl p-4 text-center">
                <div className="text-white/85 text-sm font-medium">
                  {t('nav.freeCreditsRemaining', { value: freeRemaining.toLocaleString() })}
                </div>
                <div className="text-white/55 text-[11px] mt-0.5">
                  {t('nav.renewsMonthly')}
                </div>
              </div>
            </div>
          )}
        </nav>

        {/* Footer: Help + Profile popover + Sign out */}
        <div className="mt-auto pt-6 border-t border-white/10 space-y-1">
          <NavLink
            to="/help"
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-4 px-5 py-2 rounded-xl transition-all',
                collapsed && 'justify-center px-0',
                isActive
                  ? 'bg-white/10 text-inverse-primary'
                  : 'text-white/70 hover:bg-white/5 hover:text-white',
                showHelpBanner && 'ring-1 ring-tertiary-fixed/40'
              )
            }
            title={collapsed ? t('nav.helpCenter') : undefined}
          >
            <span className="material-symbols-outlined text-[26px]">help</span>
            {!collapsed && <span className="text-[15px]">{t('nav.helpCenter')}</span>}
          </NavLink>

          {/* Profile trigger (opens popover on click) */}
          <div className="relative">
            <button
              onClick={() => setProfileOpen((o) => !o)}
              className={clsx(
                'w-full flex items-center gap-4 px-5 py-2 rounded-xl transition-all',
                collapsed && 'justify-center px-0',
                profileOpen
                  ? 'bg-white/10 text-white'
                  : 'text-white/70 hover:bg-white/5 hover:text-white'
              )}
              title={collapsed ? t('nav.myProfile') : undefined}
            >
              <span className="material-symbols-outlined text-[26px]">person</span>
              {!collapsed && (
                <>
                  <span className="text-[15px] flex-1 text-left">{t('nav.myProfile')}</span>
                  <span className="material-symbols-outlined text-lg">more_vert</span>
                </>
              )}
            </button>

            {profileOpen && (
              <>
                {/* click-outside backdrop */}
                <div
                  className="fixed inset-0 z-[90]"
                  onClick={() => setProfileOpen(false)}
                />
                <div
                  className={clsx(
                    'absolute z-[100] w-56 bg-white rounded-2xl shadow-2xl p-2',
                    // drop UPWARDS from the trigger so it never gets clipped by the sidebar bottom
                    collapsed
                      ? 'bottom-0 left-full ml-3'
                      : 'bottom-full mb-2 left-2'
                  )}
                >
                  {profileMenu.map(({ to, icon, labelKey, lockedBeforeSetup }) =>
                    !setupComplete && lockedBeforeSetup ? (
                      <div
                        key={to}
                        className="flex items-center gap-3 text-brand-dark/35 px-3 py-2.5 rounded-xl text-sm cursor-not-allowed"
                        title={t('nav.lockedUntilSetup')}
                        aria-disabled="true"
                      >
                        <MaterialIcon name={icon} size={20} />
                        {t(`nav.${labelKey}`)}
                      </div>
                    ) : (
                      <Link
                        key={to}
                        to={to}
                        className="flex items-center gap-3 text-brand-dark px-3 py-2.5 hover:bg-surface-container-low transition-all rounded-xl text-sm"
                        onClick={() => setProfileOpen(false)}
                      >
                        <MaterialIcon name={icon} size={20} />
                        {t(`nav.${labelKey}`)}
                      </Link>
                    ),
                  )}
                  <div className="mt-1 pt-1 border-t border-surface-container">
                    <LanguageSwitcher />
                  </div>
                  <div className="mt-1 pt-1 border-t border-surface-container">
                    <button
                      onClick={() => {
                        setProfileOpen(false);
                        void handleLogout();
                      }}
                      className="w-full flex items-center gap-3 text-error px-3 py-2.5 hover:bg-error-container/40 rounded-xl text-sm"
                    >
                      <MaterialIcon name="logout" size={20} />
                      {t('nav.signOut')}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main
        className={clsx(
          'transition-[margin] duration-300 min-h-screen',
          collapsed ? 'lg:ml-20' : 'lg:ml-72'
        )}
      >
        {showHelpBanner && (
          <div className="sticky top-0 lg:top-0 z-30 bg-tertiary-fixed text-brand-dark px-6 py-3 flex items-center gap-3 shadow-ambient">
            <span className="text-xl">👋</span>
            <div className="flex-1 text-sm">
              <strong className="font-semibold">{t('nav.welcomeBannerTitle')}</strong>{' '}
              {t('nav.welcomeBannerBody')}{' '}
              {/* The setup guide lives in the wizard now (T-242 removed it from Help). */}
              <Link to="/setup" className="underline font-semibold" onClick={dismissBanner}>
                {t('nav.openSetupGuide')}
              </Link>
              .
            </div>
            <button
              onClick={dismissBanner}
              aria-label={t('common.dismiss')}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-black/5"
            >
              ✕
            </button>
          </div>
        )}
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
